import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldAlert, CheckCircle2, Camera, MapPin, AlertTriangle } from 'lucide-react';
import api from '../api';

const THEME = {
    bg: '#0f172a', card: '#1e293b', border: '#334155', 
    textSec: '#94a3b8', danger: '#ef4444', success: '#10b981'
};

const PublicIncident = () => {
    const { uuid } = useParams();
    const [site, setSite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    
    // Form State
    const [formData, setFormData] = useState({
        reporter_name: '', report_type: 'HAZARD', 
        severity: 'LOW', location: '', description: ''
    });
    const [photos, setPhotos] = useState([]);
    const fileInputRef = useRef(null);

    useEffect(() => {
        api.get(`public-incident/lookup/${uuid}/`)
           .then(res => setSite(res.data))
           .catch(() => setSite(null))
           .finally(() => setLoading(false));
    }, [uuid]);

    const handleFileSelect = (e) => {
        if (e.target.files) setPhotos(prev => [...prev, ...Array.from(e.target.files)]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = new FormData();
        Object.keys(formData).forEach(k => data.append(k, formData[k]));
        photos.forEach(p => data.append('photos', p));

        try {
            await api.post(`public-incident/submit/${uuid}/`, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSubmitted(true);
        } catch(err) { alert("Failed to submit. Please try again."); }
    };

    if (loading) return <div style={{padding:'20px', background:THEME.bg, minHeight:'100vh', color:'white'}}>Loading...</div>;
    if (!site) return <div style={{padding:'20px', background:THEME.bg, minHeight:'100vh', color:'white'}}>Invalid Site Code</div>;

    if (submitted) return (
        <div style={{minHeight:'100vh', background:THEME.bg, padding:'20px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'white', textAlign:'center'}}>
            <CheckCircle2 size={60} color={THEME.success} style={{marginBottom:'20px'}}/>
            <h2>Report Received</h2>
            <p style={{color:THEME.textSec}}>The site safety team has been notified.</p>
            <p style={{color:THEME.textSec}}>Thank you for keeping the site safe.</p>
        </div>
    );

    return (
        <div style={{minHeight:'100vh', background:THEME.bg, padding:'20px', color:'white', fontFamily:'sans-serif'}}>
            <div style={{maxWidth:'600px', margin:'0 auto'}}>
                <div style={{textAlign:'center', marginBottom:'25px'}}>
                    <ShieldAlert size={40} color={THEME.danger} style={{marginBottom:'10px'}}/>
                    <h2 style={{margin:0}}>Report Safety Issue</h2>
                    <p style={{color:THEME.textSec, margin:'5px 0'}}>Site: <strong style={{color:'white'}}>{site.site_name}</strong></p>
                    <p style={{color:THEME.textSec, fontSize:'0.85rem'}}>{site.company_name}</p>
                </div>

                <form onSubmit={handleSubmit} style={{background:THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`}}>
                    
                    <label style={{color:THEME.textSec, fontSize:'0.85rem', display:'block', marginBottom:'5px'}}>Your Name (Optional)</label>
                    <input value={formData.reporter_name} onChange={e => setFormData({...formData, reporter_name: e.target.value})} placeholder="John Doe" style={inputStyle} />

                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px'}}>
                        <div>
                            <label style={{color:THEME.textSec, fontSize:'0.85rem', display:'block', marginBottom:'5px'}}>Type</label>
                            <select value={formData.report_type} onChange={e => setFormData({...formData, report_type: e.target.value})} style={inputStyle}>
                                <option value="HAZARD">Hazard</option>
                                <option value="NEAR_MISS">Near Miss</option>
                                <option value="INCIDENT">Incident</option>
                            </select>
                        </div>
                        <div>
                            <label style={{color:THEME.textSec, fontSize:'0.85rem', display:'block', marginBottom:'5px'}}>Severity</label>
                            <select value={formData.severity} onChange={e => setFormData({...formData, severity: e.target.value})} style={inputStyle}>
                                <option>LOW</option>
                                <option>HIGH</option>
                                <option>CRITICAL</option>
                            </select>
                        </div>
                    </div>

                    <label style={{color:THEME.textSec, fontSize:'0.85rem', display:'block', marginBottom:'5px'}}>Specific Location</label>
                    <div style={{position:'relative'}}>
                        <MapPin size={16} style={{position:'absolute', top:'12px', left:'10px', color:THEME.textSec}}/>
                        <input value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} placeholder="e.g. Loading Bay 2" style={{...inputStyle, paddingLeft:'35px'}} required />
                    </div>

                    <label style={{color:THEME.textSec, fontSize:'0.85rem', display:'block', marginBottom:'5px'}}>Description</label>
                    <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows="4" placeholder="Describe what happened or the hazard..." style={inputStyle} required />

                    <label style={{color:THEME.textSec, fontSize:'0.85rem', display:'block', marginBottom:'5px'}}>Photos</label>
                    <div style={{marginBottom:'10px', display:'flex', gap:'10px', flexWrap:'wrap'}}>
                        {photos.map((f, i) => (
                            <div key={i} style={{background:'rgba(255,255,255,0.1)', padding:'5px 10px', borderRadius:'4px', fontSize:'0.8rem', display:'flex', alignItems:'center', gap:'5px'}}>
                                <span>{f.name}</span>
                                <span onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))} style={{cursor:'pointer', color:THEME.danger, fontWeight:'bold'}}>×</span>
                            </div>
                        ))}
                    </div>
                    <button type="button" onClick={() => fileInputRef.current.click()} style={{...inputStyle, textAlign:'center', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}>
                        <Camera size={18}/> Add Photos
                    </button>
                    <input type="file" multiple accept="image/*" ref={fileInputRef} style={{display:'none'}} onChange={handleFileSelect} />

                    <button type="submit" style={{width:'100%', marginTop:'20px', padding:'15px', background:THEME.danger, color:'white', border:'none', borderRadius:'8px', fontSize:'1rem', fontWeight:'bold', cursor:'pointer'}}>
                        Submit Report
                    </button>
                </form>
            </div>
        </div>
    );
};

const inputStyle = { width:'100%', padding:'12px', background:'#0f172a', border:'1px solid #334155', borderRadius:'6px', color:'white', marginBottom:'15px', boxSizing:'border-box' };

export default PublicIncident;